/**
 * AGT-DSH-002 Pro dashboard copy — EN + AR.
 * LOGIN_COPY shape (per-key `{ en, ar }`) matching `components/auth/loginCopy.ts`.
 * Arabic tone: real MENA product Arabic (same register as loginCopy).
 */

export type DashboardLocale = 'en' | 'ar'

export const LOGIN_COPY = {
  'common.loading': {
    en: 'Loading',
    ar: 'جارٍ التحميل',
  },
  'greeting.morning': {
    en: 'Good morning',
    ar: 'صباح الخير',
  },
  'greeting.afternoon': {
    en: 'Good afternoon',
    ar: 'مساء الخير',
  },
  'greeting.evening': {
    en: 'Good evening',
    ar: 'مساء الخير',
  },
  'greeting.nameFallback': {
    en: 'there',
    ar: 'هناك',
  },
  'tenant.agency': {
    en: 'Agency',
    ar: 'وكالة',
  },
  'tenant.personal': {
    en: 'Personal',
    ar: 'شخصي',
  },
  'action.newListing': {
    en: 'New listing',
    ar: 'إعلان جديد',
  },
  'action.contact': {
    en: 'Contact',
    ar: 'جهة اتصال',
  },
  'action.task': {
    en: 'Task',
    ar: 'مهمة',
  },
  'action.publish': {
    en: 'Publish',
    ar: 'نشر',
  },
  'action.inbox': {
    en: 'Inbox',
    ar: 'الوارد',
  },
  'action.search': {
    en: 'Search',
    ar: 'بحث',
  },
  'action.addWidget': {
    en: 'Add widget',
    ar: 'إضافة ودجة',
  },
  'action.addWidgetPlus': {
    en: 'Add widget +',
    ar: 'إضافة ودجة +',
  },
  'action.resetLayout': {
    en: 'Reset layout',
    ar: 'إعادة تعيين التخطيط',
  },
  'action.openInbox': {
    en: 'Open inbox',
    ar: 'فتح الوارد',
  },
  'action.openTasks': {
    en: 'Open tasks',
    ar: 'فتح المهام',
  },
  'confirm.resetLayout': {
    en: 'Reset dashboard to default layout? Your current arrangement will be lost.',
    ar: 'إعادة تعيين لوحة التحكم إلى التخطيط الافتراضي؟ سيُفقد ترتيبك الحالي.',
  },
  'empty.noWidgets': {
    en: 'No widgets yet. Press `Add widget +` or use `/` to search.',
    ar: 'لا توجد ودجات بعد. اضغط «إضافة ودجة +» أو استخدم `/` للبحث.',
  },
  'save.saving': {
    en: 'Saving…',
    ar: 'جارٍ الحفظ…',
  },
  'save.saved': {
    en: 'Saved',
    ar: 'تم الحفظ',
  },
  'density.aria': {
    en: 'Dashboard density',
    ar: 'كثافة لوحة التحكم',
  },
  'density.compact': {
    en: 'compact',
    ar: 'مضغوط',
  },
  'density.comfortable': {
    en: 'comfortable',
    ar: 'مريح',
  },
  'density.spacious': {
    en: 'spacious',
    ar: 'واسع',
  },
  'aria.drag': {
    en: 'Drag {title}',
    ar: 'سحب {title}',
  },
  'aria.dragTitle': {
    en: 'Drag to rearrange',
    ar: 'اسحب لإعادة الترتيب',
  },
  'aria.fullscreen': {
    en: 'Fullscreen {title}',
    ar: 'ملء الشاشة {title}',
  },
  'aria.remove': {
    en: 'Remove {title}',
    ar: 'إزالة {title}',
  },
  'aria.removeSr': {
    en: 'Remove',
    ar: 'إزالة',
  },
  'aria.widgetGrid': {
    en: 'Pro dashboard widgets',
    ar: 'ودجات لوحة Pro',
  },
  'shortcuts.title': {
    en: 'Keyboard shortcuts',
    ar: 'اختصارات لوحة المفاتيح',
  },
  'shortcuts.reopen': {
    en: 'Press `?` any time to reopen',
    ar: 'اضغط `؟` في أي وقت لإعادة الفتح',
  },
  'shortcut.openShortcuts': {
    en: 'Open shortcuts',
    ar: 'فتح الاختصارات',
  },
  'shortcut.commandPalette': {
    en: 'Command palette',
    ar: 'لوحة الأوامر',
  },
  'shortcut.focusSearch': {
    en: 'Focus search',
    ar: 'التركيز على البحث',
  },
  'shortcut.goDashboard': {
    en: 'Go to Dashboard',
    ar: 'الانتقال إلى لوحة التحكم',
  },
  'shortcut.goInbox': {
    en: 'Go to Inbox',
    ar: 'الانتقال إلى الوارد',
  },
  'shortcut.goListings': {
    en: 'Go to Listings',
    ar: 'الانتقال إلى الإعلانات',
  },
  'shortcut.fullscreenWidget': {
    en: 'Fullscreen widget N',
    ar: 'ملء الشاشة للودجة N',
  },
  'shortcut.toggleEdit': {
    en: 'Toggle edit mode',
    ar: 'تبديل وضع التحرير',
  },
  'shortcut.exitFullscreen': {
    en: 'Exit fullscreen / edit',
    ar: 'الخروج من ملء الشاشة / التحرير',
  },
  'widget.title.kpiActive': {
    en: 'Active listings',
    ar: 'الإعلانات النشطة',
  },
  'widget.title.kpiViews': {
    en: 'Views',
    ar: 'المشاهدات',
  },
  'widget.title.kpiInquiries': {
    en: 'Inquiries',
    ar: 'الاستفسارات',
  },
  'widget.title.kpiPipeline': {
    en: 'Pipeline value',
    ar: 'قيمة المسار',
  },
  'widget.title.kpiBazaar': {
    en: 'Bazaar-driven leads',
    ar: 'عملاء من بازار',
  },
  'widget.title.urgent': {
    en: 'Urgent',
    ar: 'عاجل',
  },
  'widget.title.quota': {
    en: 'Quota / credits',
    ar: 'الحصة / الأرصدة',
  },
  'widget.title.recentListings': {
    en: 'Recent listings',
    ar: 'أحدث الإعلانات',
  },
  'widget.title.inboxPreview': {
    en: 'Inbox preview',
    ar: 'معاينة الوارد',
  },
  'widget.title.tasks': {
    en: "Today's tasks",
    ar: 'مهام اليوم',
  },
  'widget.title.funnel': {
    en: 'Funnel',
    ar: 'القمع',
  },
  'widget.title.activity': {
    en: 'Recent activity',
    ar: 'النشاط الأخير',
  },
  'widget.title.calendar': {
    en: 'Calendar',
    ar: 'التقويم',
  },
  'kpi.activeListings': {
    en: 'Active listings',
    ar: 'الإعلانات النشطة',
  },
  'kpi.viewsMtd': {
    en: 'Views (MTD)',
    ar: 'المشاهدات (هذا الشهر)',
  },
  'kpi.inquiries': {
    en: 'Inquiries',
    ar: 'الاستفسارات',
  },
  'kpi.pipelineValue': {
    en: 'Pipeline value',
    ar: 'قيمة المسار',
  },
  'kpi.bazaarLeads': {
    en: 'Bazaar-driven leads',
    ar: 'عملاء من بازار',
  },
  'kpi.delta.liveCount': {
    en: 'Live count',
    ar: 'العدد الحالي',
  },
  'kpi.delta.fromStats': {
    en: 'From dashboard stats',
    ar: 'من إحصاءات اللوحة',
  },
  'kpi.delta.openPipeline': {
    en: 'Open pipeline',
    ar: 'المسار المفتوح',
  },
  'kpi.delta.openCount': {
    en: '{count} open',
    ar: '{count} مفتوحة',
  },
  'kpi.delta.syndicated': {
    en: '{count} syndicated listings',
    ar: '{count} إعلانات مُوزَّعة',
  },
  'urgent.empty': {
    en: 'No urgent items right now.',
    ar: 'لا توجد عناصر عاجلة الآن.',
  },
  'urgent.slaBreachedOne': {
    en: '{count} SLA-breached inquiry',
    ar: 'استفسار واحد متجاوز لاتفاقية الخدمة',
  },
  'urgent.slaBreachedMany': {
    en: '{count} SLA-breached inquiries',
    ar: '{count} استفسارات متجاوزة لاتفاقية الخدمة',
  },
  'urgent.overdueTask': {
    en: 'Overdue task',
    ar: 'مهمة متأخرة',
  },
  'urgent.viewing': {
    en: 'Viewing',
    ar: 'معاينة',
  },
  'urgent.inquiry': {
    en: 'Inquiry',
    ar: 'استفسار',
  },
  'status.urgent': {
    en: 'urgent',
    ar: 'عاجل',
  },
  'status.overdue': {
    en: 'overdue',
    ar: 'متأخر',
  },
  'status.today': {
    en: 'today',
    ar: 'اليوم',
  },
  'status.new': {
    en: 'new',
    ar: 'جديد',
  },
  'quota.load': {
    en: "Today's operational load",
    ar: 'العبء التشغيلي اليوم',
  },
  'quota.dueToday': {
    en: 'Due today',
    ar: 'مستحق اليوم',
  },
  'quota.overdue': {
    en: 'Overdue',
    ar: 'متأخر',
  },
  'quota.viewings': {
    en: 'Viewings',
    ar: 'المعاينات',
  },
  'listings.empty': {
    en: 'No listings yet.',
    ar: 'لا توجد إعلانات بعد.',
  },
  'listings.fallbackTitle': {
    en: 'Listing',
    ar: 'إعلان',
  },
  'inbox.empty': {
    en: 'No unread threads.',
    ar: 'لا توجد محادثات غير مقروءة.',
  },
  'inbox.threadFallback': {
    en: 'Thread',
    ar: 'محادثة',
  },
  'inbox.badgeFallback': {
    en: 'inbox',
    ar: 'وارد',
  },
  'tasks.empty': {
    en: 'No tasks for today.',
    ar: 'لا مهام لليوم.',
  },
  'tasks.fallbackTitle': {
    en: 'Task',
    ar: 'مهمة',
  },
  'funnel.leads': {
    en: 'Leads',
    ar: 'العملاء المحتملون',
  },
  'funnel.viewings': {
    en: 'Viewings',
    ar: 'المعاينات',
  },
  'funnel.offers': {
    en: 'Offers',
    ar: 'العروض',
  },
  'funnel.closed': {
    en: 'Closed',
    ar: 'مُغلق',
  },
  'activity.inquiryRow': {
    en: 'Inquiry · {name}',
    ar: 'استفسار · {name}',
  },
  'activity.empty': {
    en: 'Activity feed empty.',
    ar: 'سجل النشاط فارغ.',
  },
  'calendar.viewingFallback': {
    en: 'Viewing',
    ar: 'معاينة',
  },
  'calendar.empty': {
    en: 'Nothing scheduled.',
    ar: 'لا توجد مواعيد قادمة.',
  },
  'calendar.error': {
    en: 'Calendar is not available — ask your admin to enable it.',
    ar: 'التقويم غير متاح — اطلب من المسؤول تفعيله.',
  },
  'calendar.loading': {
    en: 'Loading events…',
    ar: 'جارٍ تحميل المواعيد…',
  },
  'calendar.mode.upcoming': {
    en: 'Upcoming events',
    ar: 'الأحداث القادمة',
  },
  'calendar.mode.month': {
    en: 'Calendar',
    ar: 'التقويم',
  },
  'calendar.add': {
    en: 'Add appointment',
    ar: 'إضافة موعد',
  },
  'calendar.today': {
    en: 'Today',
    ar: 'اليوم',
  },
  'calendar.prevMonth': {
    en: 'Previous month',
    ar: 'الشهر السابق',
  },
  'calendar.nextMonth': {
    en: 'Next month',
    ar: 'الشهر التالي',
  },
  'calendar.tomorrow': {
    en: 'Tomorrow',
    ar: 'غدًا',
  },
  'calendar.allDay': {
    en: 'All day',
    ar: 'طوال اليوم',
  },
  'calendar.status.pending': {
    en: 'Pending',
    ar: 'قيد الانتظار',
  },
  'calendar.status.cancelled': {
    en: 'Cancelled',
    ar: 'ملغى',
  },
  'calendar.action.reschedule': {
    en: 'Reschedule',
    ar: 'إعادة الجدولة',
  },
  'calendar.action.cancel': {
    en: 'Cancel',
    ar: 'إلغاء',
  },
  'calendar.action.message': {
    en: 'Send message',
    ar: 'إرسال رسالة',
  },
  'calendar.action.reassign': {
    en: 'Reassign',
    ar: 'إسناد لآخر',
  },
  'calendar.action.delegate': {
    en: 'Delegate',
    ar: 'تفويض',
  },
  'calendar.laterWave': {
    en: 'Available in a later wave',
    ar: 'متاح في مرحلة لاحقة',
  },
  'calendar.reschedule.title': {
    en: 'Reschedule',
    ar: 'إعادة جدولة الموعد',
  },
  'calendar.reschedule.start': {
    en: 'New start',
    ar: 'البداية الجديدة',
  },
  'calendar.reschedule.end': {
    en: 'New end',
    ar: 'النهاية الجديدة',
  },
  'calendar.reschedule.optional': {
    en: 'optional',
    ar: 'اختياري',
  },
  'calendar.reschedule.seriesHelper': {
    en: 'This change applies to the whole series — editing a single occurrence is not supported yet.',
    ar: 'سيُطبَّق هذا التغيير على السلسلة بأكملها — تعديل موعد واحد من السلسلة غير متاح بعد.',
  },
  'calendar.conflict.warning': {
    en: 'Overlaps “{title}” at {time}',
    ar: 'يتعارض مع «{title}» في {time}',
  },
  'calendar.cancel.title': {
    en: 'Cancel this event?',
    ar: 'إلغاء هذا الموعد؟',
  },
  'calendar.cancel.body': {
    en: '“{title}” will be marked as cancelled.',
    ar: 'سيتم وضع علامة «ملغى» على «{title}».',
  },
  'calendar.cancel.confirm': {
    en: 'Yes, cancel event',
    ar: 'نعم، إلغاء الموعد',
  },
  'calendar.cancel.seriesBody': {
    en: '“{title}” is part of a recurring series. What do you want to cancel?',
    ar: '«{title}» جزء من سلسلة متكررة. ما الذي تريد إلغاءه؟',
  },
  'calendar.cancel.scope.this': {
    en: 'This occurrence only',
    ar: 'هذا الموعد فقط',
  },
  'calendar.cancel.scope.series': {
    en: 'The whole series',
    ar: 'السلسلة بأكملها',
  },
  'calendar.series.occurrence': {
    en: 'Recurring',
    ar: 'موعد متكرر',
  },
  'calendar.form.title': {
    en: 'Title',
    ar: 'العنوان',
  },
  'calendar.form.when': {
    en: 'When',
    ar: 'الوقت',
  },
  'calendar.form.due': {
    en: 'Due date',
    ar: 'تاريخ الاستحقاق',
  },
  'calendar.form.ends': {
    en: 'End (optional)',
    ar: 'النهاية (اختياري)',
  },
  'calendar.form.timezone': {
    en: 'Timezone',
    ar: 'المنطقة الزمنية',
  },
  'calendar.form.where': {
    en: 'Where',
    ar: 'المكان',
  },
  'calendar.form.link': {
    en: 'Link',
    ar: 'الرابط',
  },
  'calendar.form.visibility': {
    en: 'Visibility',
    ar: 'الرؤية',
  },
  'calendar.form.personal': {
    en: 'Personal',
    ar: 'شخصي',
  },
  'calendar.form.team': {
    en: 'Team',
    ar: 'الفريق',
  },
  'calendar.form.description': {
    en: 'Description',
    ar: 'الوصف',
  },
  'calendar.form.notes': {
    en: 'Notes',
    ar: 'ملاحظات',
  },
  'calendar.form.kind.meeting': {
    en: 'Meeting',
    ar: 'اجتماع',
  },
  'calendar.form.kind.todo': {
    en: 'To-do',
    ar: 'مهمة',
  },
  'calendar.form.submit': {
    en: 'Add',
    ar: 'إضافة',
  },
  'calendar.form.required': {
    en: 'This field is required',
    ar: 'هذا الحقل مطلوب',
  },
  'calendar.form.invalid': {
    en: 'Enter a valid date and time',
    ar: 'أدخل تاريخًا ووقتًا صالحَين',
  },
  'calendar.form.endsAfter': {
    en: 'End must be after start',
    ar: 'يجب أن تكون النهاية بعد البداية',
  },
  'calendar.toast.created': {
    en: 'Appointment added',
    ar: 'تمت إضافة الموعد',
  },
  'calendar.toast.updated': {
    en: 'Appointment updated',
    ar: 'تم تحديث الموعد',
  },
  'calendar.toast.cancelled': {
    en: 'Appointment cancelled',
    ar: 'تم إلغاء الموعد',
  },
  'calendar.toast.createError': {
    en: 'Could not add appointment',
    ar: 'تعذّرت إضافة الموعد',
  },
  'calendar.toast.updateError': {
    en: 'Could not update appointment',
    ar: 'تعذّر تحديث الموعد',
  },
  'calendar.toast.cancelError': {
    en: 'Could not cancel appointment',
    ar: 'تعذّر إلغاء الموعد',
  },
  // Wave 2 — diary page
  'calendar.diary.title': {
    en: 'Calendar & Diary',
    ar: 'التقويم واليوميات',
  },
  'calendar.openDiary': {
    en: 'Open full diary',
    ar: 'افتح اليوميات كاملة',
  },
  'calendar.view.day': {
    en: 'Day',
    ar: 'يوم',
  },
  'calendar.view.week': {
    en: 'Week',
    ar: 'أسبوع',
  },
  'calendar.view.month': {
    en: 'Month',
    ar: 'شهر',
  },
  'calendar.view.schedule': {
    en: 'Schedule',
    ar: 'جدول',
  },
  'calendar.pager.today': {
    en: 'Today',
    ar: 'اليوم',
  },
  'calendar.pager.previous': {
    en: 'Previous period',
    ar: 'الفترة السابقة',
  },
  'calendar.pager.next': {
    en: 'Next period',
    ar: 'الفترة التالية',
  },
  'calendar.jump.previous': {
    en: 'Previous appointment',
    ar: 'الموعد السابق',
  },
  'calendar.jump.next': {
    en: 'Next appointment',
    ar: 'الموعد التالي',
  },
  'calendar.calendars.my': {
    en: 'My Calendars',
    ar: 'تقويماتي',
  },
  'calendar.kind.viewing': {
    en: 'Viewings',
    ar: 'المعاينات',
  },
  'calendar.kind.meeting': {
    en: 'Meetings',
    ar: 'الاجتماعات',
  },
  'calendar.kind.todo': {
    en: 'To-dos',
    ar: 'المهام',
  },
  'calendar.kind.call': {
    en: 'Calls',
    ar: 'المكالمات',
  },
  'calendar.kind.custom': {
    en: 'Custom',
    ar: 'مخصص',
  },
  'calendar.emptyDay': {
    en: 'Nothing scheduled for this day.',
    ar: 'لا مواعيد في هذا اليوم.',
  },
  'calendar.keyboardHint': {
    en: 'Keyboard: D / W / M / S switch views · ← / → move the period',
    ar: 'لوحة المفاتيح: D / W / M / S لتبديل العرض · ← / → لتحريك الفترة',
  },
  // Wave 3 — quick actions + rules surfacing
  'calendar.memberAction.reassign.title': {
    en: 'Reassign to…',
    ar: 'إسناد إلى…',
  },
  'calendar.memberAction.delegate.title': {
    en: 'Delegate to…',
    ar: 'تفويض إلى…',
  },
  'calendar.memberAction.reassign.helper': {
    en: 'Reassign moves it — ownership transfers and you become an optional attendee.',
    ar: 'الإسناد ينقل الموعد — تنتقل الملكية وتصبح حاضرًا اختياريًا.',
  },
  'calendar.memberAction.delegate.helper': {
    en: 'Delegate lends it — you keep the event; a proxy can act on your behalf.',
    ar: 'التفويض يُعيره — تبقى الملكية لك ويمكن للوكيل التصرف نيابة عنك.',
  },
  'calendar.memberAction.empty': {
    en: 'No team members found.',
    ar: 'لا يوجد أعضاء في الفريق.',
  },
  'calendar.memberAction.confirm': {
    en: 'Confirm',
    ar: 'تأكيد',
  },
  'calendar.noLinkedContact': {
    en: 'No linked contact for this event.',
    ar: 'لا يوجد جهة اتصال مرتبطة بهذا الموعد.',
  },
  'calendar.sendMessage.noConversation': {
    en: 'No conversation with this contact yet — start one from the inbox.',
    ar: 'لا محادثة مع هذه الجهة بعد — ابدأ واحدة من صندوق الوارد.',
  },
  'calendar.ruleError.title': {
    en: 'Cannot schedule:',
    ar: 'تعذّر جدولة الموعد:',
  },
  // Wave 4 — calendar settings + autoresponse templates
  'calendar.settings.title': {
    en: 'Calendar settings',
    ar: 'إعدادات التقويم',
  },
  'calendar.settings.open': {
    en: 'Calendar settings',
    ar: 'إعدادات التقويم',
  },
  'calendar.settings.tab.rules': {
    en: 'Rules',
    ar: 'القواعد',
  },
  'calendar.settings.tab.templates': {
    en: 'Autoresponses',
    ar: 'الردود التلقائية',
  },
  'calendar.settings.loading': {
    en: 'Loading settings…',
    ar: 'جارٍ تحميل الإعدادات…',
  },
  'calendar.settings.error': {
    en: 'Could not load settings.',
    ar: 'تعذّر تحميل الإعدادات.',
  },
  'calendar.settings.retry': {
    en: 'Retry',
    ar: 'إعادة المحاولة',
  },
  'calendar.settings.timezone': {
    en: 'Timezone',
    ar: 'المنطقة الزمنية',
  },
  'calendar.settings.operatingHours': {
    en: 'Operating hours',
    ar: 'ساعات العمل',
  },
  'calendar.settings.operatingHours.helper': {
    en: 'Open the days you work — closed days are excluded. If every day is closed, no working-hours limit applies.',
    ar: 'فعّل أيام عملك — الأيام المغلقة مستثناة. إذا كانت جميع الأيام مغلقة فلا يُطبَّق أي حد على ساعات العمل.',
  },
  'calendar.settings.day.open': {
    en: 'Open',
    ar: 'مفتوح',
  },
  'calendar.settings.start': {
    en: 'Start',
    ar: 'البداية',
  },
  'calendar.settings.end': {
    en: 'End',
    ar: 'النهاية',
  },
  'calendar.settings.buffer': {
    en: 'Buffer between appointments (minutes)',
    ar: 'فاصل بين المواعيد (بالدقائق)',
  },
  'calendar.settings.minNotice': {
    en: 'Minimum booking notice (minutes)',
    ar: 'أقل مدة إشعار مسبق للحجز (بالدقائق)',
  },
  'calendar.settings.maxPerDay': {
    en: 'Maximum appointments per day',
    ar: 'أقصى عدد مواعيد في اليوم',
  },
  'calendar.settings.notEnforced': {
    en: '0 = not enforced',
    ar: '0 = غير مطبَّق',
  },
  'calendar.settings.aiGuidelines': {
    en: 'AI booking guidelines',
    ar: 'إرشادات الحجز بالذكاء الاصطناعي',
  },
  'calendar.settings.aiGuidelines.helper': {
    en: 'Saved with your settings; these take effect once the AI booking feature arrives in a later wave.',
    ar: 'تُحفظ مع إعداداتك وتسري عند إطلاق ميزة الحجز بالذكاء الاصطناعي في مرحلة لاحقة.',
  },
  'calendar.settings.save': {
    en: 'Save rules',
    ar: 'حفظ القواعد',
  },
  'calendar.settings.saved': {
    en: 'Settings saved',
    ar: 'تم حفظ الإعدادات',
  },
  'calendar.settings.saveError': {
    en: 'Could not save settings.',
    ar: 'تعذّر حفظ الإعدادات.',
  },
  'calendar.templates.empty': {
    en: 'No autoresponse templates yet.',
    ar: 'لا توجد قوالب ردود تلقائية بعد.',
  },
  'calendar.templates.loading': {
    en: 'Loading templates…',
    ar: 'جارٍ تحميل القوالب…',
  },
  'calendar.templates.error': {
    en: 'Could not load templates.',
    ar: 'تعذّر تحميل القوالب.',
  },
  'calendar.templates.new': {
    en: 'New template',
    ar: 'قالب جديد',
  },
  'calendar.templates.name': {
    en: 'Name',
    ar: 'الاسم',
  },
  'calendar.templates.trigger': {
    en: 'Trigger',
    ar: 'المُشغِّل',
  },
  'calendar.templates.channel': {
    en: 'Channel',
    ar: 'القناة',
  },
  'calendar.templates.body': {
    en: 'Message',
    ar: 'الرسالة',
  },
  'calendar.templates.body.hint': {
    en: 'Placeholders: {event_title} · {event_time} · {owner_name}',
    ar: 'الحقول المتاحة: {event_title} · {event_time} · {owner_name}',
  },
  'calendar.templates.trigger.on_booked': {
    en: 'When booked',
    ar: 'عند الحجز',
  },
  'calendar.templates.trigger.on_reschedule': {
    en: 'When rescheduled',
    ar: 'عند إعادة الجدولة',
  },
  'calendar.templates.trigger.on_cancel': {
    en: 'When cancelled',
    ar: 'عند الإلغاء',
  },
  'calendar.templates.channel.whatsapp': {
    en: 'WhatsApp',
    ar: 'واتساب',
  },
  'calendar.templates.channel.in_app': {
    en: 'In-app',
    ar: 'داخل التطبيق',
  },
  'calendar.templates.channel.email': {
    en: 'Email',
    ar: 'البريد الإلكتروني',
  },
  'calendar.templates.whatsappQueued': {
    en: 'Queued — WhatsApp template approval pending',
    ar: 'قيد الانتظار — بانتظار اعتماد قالب واتساب',
  },
  'calendar.templates.active': {
    en: 'Active',
    ar: 'مفعّل',
  },
  'calendar.templates.edit': {
    en: 'Edit',
    ar: 'تعديل',
  },
  'calendar.templates.delete': {
    en: 'Delete',
    ar: 'حذف',
  },
  'calendar.templates.deleteTitle': {
    en: 'Delete template?',
    ar: 'حذف القالب؟',
  },
  'calendar.templates.deleteBody': {
    en: 'Delete “{name}”? This cannot be undone.',
    ar: 'حذف «{name}»؟ لا يمكن التراجع عن هذا الإجراء.',
  },
  'calendar.templates.save': {
    en: 'Save template',
    ar: 'حفظ القالب',
  },
  'calendar.templates.created': {
    en: 'Template saved',
    ar: 'تم حفظ القالب',
  },
  'calendar.templates.createError': {
    en: 'Could not save template',
    ar: 'تعذّر حفظ القالب',
  },
  'calendar.templates.deleted': {
    en: 'Template deleted',
    ar: 'تم حذف القالب',
  },
  'calendar.templates.deleteError': {
    en: 'Could not delete template',
    ar: 'تعذّر حذف القالب',
  },
  'calendar.templates.toggleError': {
    en: 'Could not update the template',
    ar: 'تعذّر تحديث القالب',
  },
  // Wave 5 — team calendar (day/week views)
  'calendar.team.section': {
    en: 'Team',
    ar: 'الفريق',
  },
  'calendar.team.toggle': {
    en: 'Show team events',
    ar: 'إظهار مواعيد الفريق',
  },
  'calendar.team.soloDisabled': {
    en: 'No team members with shared events.',
    ar: 'لا يوجد أعضاء فريق لديهم مواعيد مشتركة.',
  },
  'calendar.team.stripTitle': {
    en: "Who's where today",
    ar: 'أين الفريق اليوم',
  },
  'calendar.team.empty': {
    en: 'No team events in this period.',
    ar: 'لا مواعيد للفريق في هذه الفترة.',
  },
  'calendar.team.error': {
    en: 'Team calendar failed to load.',
    ar: 'تعذّر تحميل تقويم الفريق.',
  },
  'calendar.team.memberFallback': {
    en: 'Team member',
    ar: 'عضو الفريق',
  },
  'widget.unavailable': {
    en: 'Widget unavailable.',
    ar: 'الودجة غير متاحة.',
  },
} as const

export type DashboardCopyKey = keyof typeof LOGIN_COPY

export function t(
  key: DashboardCopyKey,
  locale: DashboardLocale,
  vars?: Record<string, string | number>,
): string {
  let value: string = LOGIN_COPY[key][locale]
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      value = value.replace(`{${k}}`, String(v))
    }
  }
  return value
}

/** Widget grid title keys keyed by layout widget id. */
export const WIDGET_TITLE_KEYS: Record<string, DashboardCopyKey> = {
  'kpi-active': 'widget.title.kpiActive',
  'kpi-views': 'widget.title.kpiViews',
  'kpi-inquiries': 'widget.title.kpiInquiries',
  'kpi-pipeline': 'widget.title.kpiPipeline',
  'kpi-bazaar': 'widget.title.kpiBazaar',
  urgent: 'widget.title.urgent',
  quota: 'widget.title.quota',
  'recent-listings': 'widget.title.recentListings',
  'inbox-preview': 'widget.title.inboxPreview',
  tasks: 'widget.title.tasks',
  funnel: 'widget.title.funnel',
  activity: 'widget.title.activity',
  calendar: 'widget.title.calendar',
}
